<script setup>
import { computed } from 'vue';
import ChannelSelector from '../ChannelSelector.vue';
import { IS_INSTAGRAM_WHATSAPP_INBOX_CREATION_DISABLED } from 'dashboard/constants/globals';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  channel: {
    type: Object,
    required: true,
  },
  enabledFeatures: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['channelItemClick']);

const hasFbConfigured = computed(() => {
  const config = window.chatspaceConfig || window.chatwootConfig || {};
  return config.fbAppId;
});

const hasInstagramConfigured = computed(() => {
  const config = window.chatspaceConfig || window.chatwootConfig || {};
  return !IS_INSTAGRAM_WHATSAPP_INBOX_CREATION_DISABLED && config.instagramAppId;
});

const hasTiktokConfigured = computed(() => {
  const config = window.chatspaceConfig || window.chatwootConfig || {};
  return config.tiktokAppId;
});

const isLocked = computed(() => {
  return props.channel.key === 'voice';
});

const isActive = computed(() => {
  const { key } = props.channel;
  if (key === 'voice') {
    return false; // Locked/managed by administrator
  }
  if (Object.keys(props.enabledFeatures).length === 0) {
    return false;
  }
  if (key === 'website') {
    return props.enabledFeatures.channel_website;
  }
  if (key === 'facebook') {
    return props.enabledFeatures.channel_facebook && hasFbConfigured.value;
  }
  if (key === 'email') {
    return props.enabledFeatures.channel_email;
  }

  if (key === 'instagram') {
    return (
      props.enabledFeatures.channel_instagram && hasInstagramConfigured.value
    );
  }

  if (key === 'tiktok') {
    return props.enabledFeatures.channel_tiktok && hasTiktokConfigured.value;
  }

  if (key === 'whatsapp_call') {
    const config = window.chatspaceConfig || window.chatwootConfig || {};
    return (
      !IS_INSTAGRAM_WHATSAPP_INBOX_CREATION_DISABLED &&
      props.enabledFeatures.channel_voice &&
      !!config.whatsappAppId &&
      config.whatsappAppId !== 'none'
    );
  }
  return [
    'website',
    'twilio',
    'api',
    'whatsapp',
    'sms',
    'telegram',
    'line',
    'instagram',
    'tiktok',
  ].includes(key);
});

const isComingSoon = computed(() => false);

const isBeta = computed(() => {
  return ['tiktok', 'whatsapp_call'].includes(props.channel.key);
});

const hasVoiceBadge = computed(() => {
  return (
    ['voice', 'whatsapp_call'].includes(props.channel.key) &&
    !!props.enabledFeatures.channel_voice
  );
});

const onItemClick = () => {
  if (isLocked.value) {
    useAlert(
      'FreeSWITCH Telephony lines are provisioned and managed directly by the system administrator.'
    );
    return;
  }
  if (isActive.value) {
    emit('channelItemClick', props.channel.key);
  }
};
</script>

<template>
  <ChannelSelector
    :title="channel.title"
    :description="channel.description"
    :icon="channel.icon"
    :is-coming-soon="isComingSoon"
    :is-beta="isBeta"
    :is-locked="isLocked"
    :has-voice-badge="hasVoiceBadge"
    :disabled="!isActive && !isLocked"
    @click="onItemClick"
  />
</template>
